import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, HeartIcon, LeafIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { IMAGES, TRUST_ITEMS } from '../data/content';
import { Eyebrow, Reveal, Section } from '../components/Section';
const VALUES = [{
  icon: SparklesIcon,
  title: 'Make wellness simpler',
  copy: 'A good routine should fit into real life — not demand a shelf full of bottles or a perfect schedule.'
}, {
  icon: LeafIcon,
  title: 'Let nature lead',
  copy: 'We begin with plant botanicals that have long been part of everyday Indian wellness, then formulate with care.'
}, {
  icon: ShieldCheckIcon,
  title: 'Choose with intention',
  copy: 'Every ingredient earns its place. No added sugar, no unnecessary complexity, and clear reasons behind every choice.'
}];
const MILESTONES = [{
  number: '01',
  title: 'Listen',
  copy: 'We started with a simple observation: people want to feel better, but daily nutrition has become too complicated.'
}, {
  number: '02',
  title: 'Simplify',
  copy: 'We brought together plant botanicals, vitamins and minerals into one portable daily sachet.'
}, {
  number: '03',
  title: 'Make it stick',
  copy: 'We designed Medufit around the smallest sustainable habit: one minute, one glass of water, every morning.'
}];
const PROMISES = ['Plant-based daily nutrition', '21 essential vitamins & minerals', 'Zero added sugar', 'Made in an FDA registered facility'];
export function AboutUs() {
  return <main className="overflow-hidden pt-20" data-id="element-324" data-mp="1ryjfyn6wot5f">
      <section className="relative min-h-[620px] w-full overflow-hidden bg-forest-deep px-6 py-16 text-ivory md:px-10 md:py-20 lg:px-16" data-id="element-325" data-mp="25wht5p9hqjjc">
        <img src={IMAGES.aboutHero} alt="A woman preparing a green wellness drink in her sunlit kitchen" className="absolute inset-0 h-full w-full object-cover object-[68%_center] opacity-75" data-id="element-326" data-mp="20wvfxftnx3aj" />
        <div className="absolute inset-0 bg-forest-deep/45" aria-hidden="true" data-id="element-327" data-mp="3cewjoh0xu6mt" />
        <div className="absolute inset-0 bg-forest-deep/20" aria-hidden="true" data-id="element-328" data-mp="29yvxxpqg7cq1" />

        <div className="relative mx-auto flex min-h-[490px] max-w-7xl flex-col justify-end" data-id="element-329" data-mp="2exfjhp14qmhv">
          <Reveal className="max-w-3xl" data-id="element-330" data-mp="2j158gflpv63f">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage" data-id="element-331" data-mp="2ixzgmslpdq40">About Medufit</p>
            <h1 className="mt-5 font-serif text-5xl font-medium leading-[0.98] tracking-tight text-ivory sm:text-6xl lg:text-7xl" data-id="element-332" data-mp="2nwsyex80w39z">
              Wellness, made wonderfully simple.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-ivory/85" data-id="element-333" data-mp="1v8evc322ugrk">
              We’re here to make daily nutrition feel less like a chore and more like a small act of
              care — for yourself, your day, and the people who count on you.
            </p>
          </Reveal>
        </div>
      </section>

      <Section className="py-16 md:py-20" data-id="element-334" data-mp="1cjqgnyu6t7o8">
        <div className="grid items-center gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:gap-20" data-id="element-335" data-mp="1cip3notm2b3v">
          <Reveal data-id="element-336" data-mp="14zfsbq1jo2c6">
            <Eyebrow data-id="element-337" data-mp="14zpnds1g4zoq">Why we started</Eyebrow>
            <h2 className="mt-5 font-serif text-4xl font-medium leading-tight text-forest sm:text-5xl" data-id="element-338" data-mp="101nuygi4ro0l">
              We don’t believe feeling your best should be complicated.
            </h2>
          </Reveal>
          <Reveal delay={0.08} className="space-y-5 text-base leading-relaxed text-forest/75 sm:text-lg" data-id="element-339" data-mp="r198hdtfk2vi">
            <p data-id="element-340" data-mp="m1whcgvj09n4">
              Somewhere along the way, wellness became overwhelming. More bottles. More powders.
              More advice that was hard to follow — and even harder to keep up with.
            </p>
            <p data-id="element-341" data-mp="ccle3avblz75">
              Medufit was created to bring it back to the essentials: considered nutrition from
              plant botanicals, essential vitamins and minerals, in one simple sachet you can use
              every day.
            </p>
            <p className="font-serif text-2xl italic text-forest" data-id="element-342" data-mp="3uhg8nacmzhzj">Mix. Drink. Get on with your day.</p>
          </Reveal>
        </div>
      </Section>

      <Section className="pb-16 md:pb-20" data-id="element-343" data-mp="1yfl6pvibd1kj">
        <div className="grid items-center overflow-hidden rounded-[2rem] bg-beige/40 lg:grid-cols-2" data-id="element-344" data-mp="1ygo0qcaxnk4o">
          <Reveal className="order-2 p-8 sm:p-12 lg:order-1 lg:p-16" data-id="element-345" data-mp="1ooi8r2kol2ph">
            <Eyebrow data-id="element-346" data-mp="1oo8h7ajzmnho">From source to sachet</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight text-forest sm:text-4xl" data-id="element-347" data-mp="1tn1y7cyhgwml">
              Thoughtful ingredients, honest choices.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-forest/70" data-id="element-348" data-mp="1jpe14gdend19">
              Our formulations connect time-tested plant botanicals with the daily nutritional
              essentials your body uses. We focus on what matters — and leave out what doesn’t.
            </p>
            <ul className="mt-7 space-y-3" data-id="element-349" data-mp="1aqk62ufq2upt">
              {PROMISES.map(promise => <li key={promise} className="flex items-center gap-3 text-sm font-medium text-forest" data-id="element-350" data-mp="301kltnnkna4w">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-forest text-ivory" data-id="element-351" data-mp="350e3ls9w5nav">
                    <CheckIcon className="h-3.5 w-3.5" data-id="element-352" data-mp="3a7kluiptvmfr" />
                  </span>
                  {promise}
                </li>)}
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="order-1 h-full min-h-[330px] lg:order-2" data-id="element-353" data-mp="3o13ihz7fs10a">
            <img src={IMAGES.aboutSource} alt="Amla, ashwagandha and other botanical ingredients arranged on stone" className="h-full w-full object-cover" data-id="element-354" data-mp="3j6z979ap7gw1" />
          </Reveal>
        </div>
      </Section>

      <section className="bg-forest-deep px-6 py-16 text-ivory md:px-10 md:py-20 lg:px-16" data-id="element-355" data-mp="asyxe47f6fkc">
        <div className="mx-auto max-w-7xl" data-id="element-356" data-mp="6qc5k3ar0un5">
          <Reveal className="max-w-2xl" data-id="element-357" data-mp="6rdjqgn29x93">
            <Eyebrow data-id="element-358" data-mp="2cjk6pifsipq4">What guides us</Eyebrow>
            <h2 className="mt-5 font-serif text-4xl font-medium leading-tight text-ivory sm:text-5xl" data-id="element-359" data-mp="2cklmg07r40e6">
              Better habits begin with better principles.
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-360" data-mp="biw2767py2pq">
            {VALUES.map((value, index) => {
            const Icon = value.icon;
            return <Reveal key={value.title} delay={index * 0.08} data-id="element-361" data-mp="lf8vi2zcqejy" data-mpi={index}>
                  <article className="h-full rounded-2xl border border-ivory/15 bg-forest/35 p-7" data-id="element-362" data-mp="li4vdwurfdb1">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ivory/10 text-sage" data-id="element-363" data-mp="qciizd36kg55">
                      <Icon className="h-5 w-5" data-id="element-364" data-mp="zf9vzhbpfjyw" />
                    </span>
                    <h3 className="mt-6 font-serif text-2xl text-ivory" data-id="element-365" data-mp="zdgqw6gqp7t0">{value.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ivory/72" data-id="element-366" data-mp="148ct1zfoxbju">{value.copy}</p>
                  </article>
                </Reveal>;
          })}
          </div>
        </div>
      </section>

      <Section className="py-16 md:py-20" data-id="element-367" data-mp="1e2mo5ow0ony9">
        <div className="grid items-start gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20" data-id="element-368" data-mp="1i72qjg6f9d8q">
          <Reveal data-id="element-369" data-mp="32obv4ubozyny">
            <Eyebrow data-id="element-370" data-mp="32ov25fzt12n7">Our approach</Eyebrow>
            <h2 className="mt-5 font-serif text-4xl font-medium leading-tight text-forest sm:text-5xl" data-id="element-371" data-mp="32lquu9r1lqsb">
              One daily habit. A lasting difference.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-forest/70" data-id="element-372" data-mp="2tnngbxeh5ua0">
              We build products for the long run. No extremes, no complicated programs — just a
              consistent practice that can meet you wherever your day begins.
            </p>
          </Reveal>
          <div className="divide-y divide-forest/12 border-t border-forest/12" data-id="element-373" data-mp="3hdhssduvtgtq">
            {MILESTONES.map((milestone, index) => <Reveal key={milestone.number} delay={index * 0.08} data-id="element-374" data-mp="37n6dxw9ntn82" data-mpi={index}>
                <article className="grid grid-cols-[auto_1fr] gap-5 py-7" data-id="element-375" data-mp="37mwl80x8d1yo">
                  <span className="font-serif text-4xl text-sage/65" data-id="element-376" data-mp="8y7snbajylol">{milestone.number}</span>
                  <div data-id="element-377" data-mp="45eqmv6gxrg7">
                    <h3 className="font-serif text-2xl text-forest" data-id="element-378" data-mp="44v574hm0kxf">{milestone.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-forest/70" data-id="element-379" data-mp="38mpql1ch7qy0">{milestone.copy}</p>
                  </div>
                </article>
              </Reveal>)}
          </div>
        </div>
      </Section>

      <Section className="pb-16 md:pb-20" data-id="element-380" data-mp="2zlhvst96588o">
        <div className="grid items-center gap-8 overflow-hidden rounded-[2rem] bg-forest/8 lg:grid-cols-[1fr_1.1fr]" data-id="element-381" data-mp="33oehwtxx3mhd">
          <Reveal className="h-full min-h-[300px]" data-id="element-382" data-mp="3rg21eqsunv9m">
            <img src={IMAGES.aboutCommunity} alt="Friends sharing breakfast together in a bright, relaxed dining space" className="h-full w-full object-cover" data-id="element-383" data-mp="8h1w4d2rkb4" />
          </Reveal>
          <Reveal delay={0.1} className="p-8 sm:p-12" data-id="element-384" data-mp="3nc40qbnvmceg">
            <Eyebrow data-id="element-385" data-mp="3nezwbtc94ozq">For real life</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight text-forest sm:text-4xl" data-id="element-386" data-mp="26xvpvecgxrin">
              Built for every kind of morning.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-forest/70" data-id="element-387" data-mp="23gpt4d2db3gg">
              The rushed ones. The slow ones. The days at home and the days in between. Medufit is
              here to make your routine feel like one thing you can always return to.
            </p>
            <Link to="/#packages" className="mt-7 inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-forest-deep" data-id="element-388" data-mp="2dct6kf2o6oip">
              Discover Medufit <ArrowRightIcon className="h-4 w-4" data-id="element-389" data-mp="1fsgbzog3n3vn" />
            </Link>
          </Reveal>
        </div>
      </Section>

      <section className="border-y border-forest/10 bg-ivory px-6 py-8 md:px-10 lg:px-16" data-id="element-390" data-mp="1uhw1z27t35aa">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-x-8 gap-y-4" data-id="element-391" data-mp="1pot7mrv7frt1">
          {TRUST_ITEMS.map(item => <span key={item.label} className="flex items-center gap-2 text-sm font-medium text-forest/75" data-id="element-392" data-mp="11xxki81p1ks4">
              <span aria-hidden="true" data-id="element-393" data-mp="wz2y6w4ipe39">{item.icon}</span>
              {item.label}
            </span>)}
        </div>
      </section>

      <section className="bg-forest-deep px-6 py-16 text-center text-ivory md:px-10 md:py-20 lg:px-16" data-id="element-394" data-mp="1nmdv0nczz91k">
        <Reveal className="mx-auto max-w-2xl" data-id="element-395" data-mp="1il7yeeg7v1mr">
          <HeartIcon className="mx-auto h-6 w-6 text-sage" data-id="element-396" data-mp="1io2n4i69j8lz" />
          <h2 className="mt-5 font-serif text-4xl font-medium leading-tight sm:text-5xl" data-id="element-397" data-mp="1dmdbb0b7uqwu">
            A little better, every day.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-ivory/75" data-id="element-398" data-mp="14uuvwrm3gf4t">
            That’s the future we’re building — one simple, nourishing ritual at a time.
          </p>
          <Link to="/#packages" className="mt-8 inline-flex items-center gap-2 rounded-full bg-ivory px-7 py-3.5 text-sm font-semibold text-forest transition-colors hover:bg-beige" data-id="element-399" data-mp="ztf5sl527ifk">
            Shop Medufit <ArrowRightIcon className="h-4 w-4" data-id="element-400" data-mp="lvgyfzp5om9k" />
          </Link>
        </Reveal>
      </section>
    </main>;
}